import React, { useState } from 'react';
import { Select, Group, ActionIcon, Tooltip, Box, Text, Badge } from '@mantine/core';
import {
    IconPalette,
    IconSun,
    IconMoon,
    IconDeviceDesktop,
    IconColorSwatch,
} from '@tabler/icons-react';
import { useTheme, ThemeName } from '../contexts/ThemeContext';

const themeOptions: Array<{
    value: ThemeName;
    label: string;
    icon: React.ReactNode;
    description: string;
}> = [
    { value: 'system', label: 'System', icon: <IconDeviceDesktop size={16} />, description: 'Follow system preference' },
    { value: 'light', label: 'Light', icon: <IconSun size={16} />, description: 'Light theme' },
    { value: 'dark', label: 'Dark', icon: <IconMoon size={16} />, description: 'Dark theme' },
    { value: 'blue', label: 'Blue', icon: <IconColorSwatch size={16} />, description: 'Blue accent theme' },
    { value: 'green', label: 'Green', icon: <IconColorSwatch size={16} />, description: 'Green accent theme' },
    { value: 'purple', label: 'Purple', icon: <IconColorSwatch size={16} />, description: 'Purple accent theme' },
    { value: 'orange', label: 'Orange', icon: <IconColorSwatch size={16} />, description: 'Orange accent theme' },
];

interface ThemeSelectorProps {
    variant?: 'select' | 'chips' | 'compact';
}

export const ThemeSelector: React.FC<ThemeSelectorProps> = ({ variant = 'select' }) => {
    const { themeName, setThemeName } = useTheme();
    const [showCompactSelector, setShowCompactSelector] = useState(false);

    if (variant === 'chips') {
        return (
            <Group gap="xs" wrap="wrap">
                {themeOptions.map((option) => (
                    <Tooltip key={option.value} label={option.description}>
                        <Badge
                            leftSection={option.icon}
                            variant={themeName === option.value ? 'filled' : 'outline'}
                            style={{ cursor: 'pointer' }}
                            onClick={() => setThemeName(option.value)}
                        >
                            {option.label}
                        </Badge>
                    </Tooltip>
                ))}
            </Group>
        );
    }

    if (variant === 'compact') {
        return (
            <Group gap="xs" align="center">
                <Tooltip label={showCompactSelector ? 'Hide theme selector' : 'Show theme selector'}>
                    <ActionIcon
                        variant="subtle"
                        color="gray"
                        size="sm"
                        onClick={() => setShowCompactSelector((v) => !v)}
                        aria-label={showCompactSelector ? 'Hide theme selector' : 'Show theme selector'}
                    >
                        <IconPalette size={16} />
                    </ActionIcon>
                </Tooltip>
                {showCompactSelector && (
                    <Select
                        size="xs"
                        value={themeName}
                        onChange={(val) => val && setThemeName(val as ThemeName)}
                        data={themeOptions.map((o) => ({ value: o.value, label: o.label }))}
                        w={120}
                    />
                )}
            </Group>
        );
    }

    return (
        <Group gap="sm" align="center">
            <Group gap={4} align="center">
                <IconPalette size={18} />
                <Text size="sm" fw={500}>Theme</Text>
            </Group>
            <Select
                size="sm"
                value={themeName}
                onChange={(val) => val && setThemeName(val as ThemeName)}
                data={themeOptions.map((o) => ({ value: o.value, label: o.label }))}
                w={140}
            />
        </Group>
    );
};
